import React from 'react'

const MEAL_LABELS_FR = { breakfast: 'Petit-déj', lunch: 'Déjeuner', dinner: 'Dîner' }
const MEAL_ICONS = { breakfast: '🥐', lunch: '🥗', dinner: '🍝' }

/**
 * Compute the next "live" slot for the household:
 *   - Today's upcoming meal type (based on current hour)
 *   - Else tomorrow's breakfast
 * Returns { dayOfWeek, mealType, slot|null, label }
 */
export function findNextMealContext({ slots = [], todayDayOfWeek, hour }) {
  // Day-of-week in JS: Sun=0..Sat=6. We store Monday-indexed: Mon=0..Sun=6.
  const todayIdx = todayDayOfWeek
  const order = ['breakfast', 'lunch', 'dinner']
  const cutoffs = { breakfast: 10, lunch: 14, dinner: 21 } // until-hour for "still relevant today"

  for (const meal of order) {
    if (hour < cutoffs[meal]) {
      const slot = slots.find((s) => s.day_of_week === todayIdx && s.meal_type === meal)
      return { dayOfWeek: todayIdx, mealType: meal, slot: slot ?? null, when: 'today' }
    }
  }
  // Past dinner — show tomorrow's breakfast
  const nextDay = (todayIdx + 1) % 7
  const slot = slots.find((s) => s.day_of_week === nextDay && s.meal_type === 'breakfast')
  return { dayOfWeek: nextDay, mealType: 'breakfast', slot: slot ?? null, when: 'tomorrow' }
}

export function NextMealHero({
  slots = [],
  todayDayOfWeek,
  isCurrentWeek,
  onPrepared,
  onSkip,
  onSuggest,
  onAdd,
}) {
  // Hide hero entirely on past/future weeks — context loses meaning.
  if (!isCurrentWeek) return null

  const hour = new Date().getHours()
  const ctx = findNextMealContext({ slots, todayDayOfWeek, hour })
  const { mealType, slot, when } = ctx

  const whenLabel =
    when === 'today'
      ? mealType === 'dinner'
        ? 'Ce soir'
        : mealType === 'lunch'
          ? 'À midi'
          : 'Ce matin'
      : `Demain · ${MEAL_LABELS_FR[mealType]}`

  if (!slot || slot.status === 'skipped') {
    return (
      <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-200 p-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">{whenLabel}</p>
          <p className="text-sm text-emerald-900/70 mt-1">Rien de prévu — qu'est-ce qu'on mange ?</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => onAdd?.({ day: ctx.dayOfWeek, mealType })}
            className="px-3 py-2 bg-white text-emerald-700 text-xs font-semibold rounded-lg border border-emerald-200 hover:bg-emerald-50 transition-colors"
          >
            + Ajouter
          </button>
          <button
            onClick={onSuggest}
            className="px-3 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
          >
            💡 Suggérer
          </button>
        </div>
      </div>
    )
  }

  if (slot.status === 'prepared') {
    return (
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4 flex items-center gap-3">
        <span className="text-3xl">✅</span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide opacity-90">{whenLabel}</p>
          <p className="font-semibold truncate">{slot.custom_meal}</p>
          <p className="text-xs opacity-80 mt-0.5">Préparé · bon appétit !</p>
        </div>
      </div>
    )
  }

  // Planned
  return (
    <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-4 shadow-md">
      <div className="flex items-start gap-3">
        <span className="text-3xl flex-shrink-0">{MEAL_ICONS[mealType]}</span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide opacity-90">{whenLabel}</p>
          <p className="font-bold text-lg truncate">{slot.custom_meal}</p>
          {slot.prep_time && (
            <p className="text-xs opacity-80 mt-0.5">~{slot.prep_time} min de prep</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onPrepared?.(slot)}
          className="flex-1 px-3 py-2 bg-white text-emerald-700 text-sm font-semibold rounded-lg hover:bg-emerald-50 transition-colors"
        >
          ✓ Préparé
        </button>
        <button
          onClick={() => onSkip?.(slot)}
          className="px-3 py-2 bg-emerald-800/40 text-white text-sm font-medium rounded-lg hover:bg-emerald-800/60 transition-colors"
        >
          Passer
        </button>
      </div>
    </div>
  )
}
